"use client";

import { motion, AnimatePresence } from "motion/react";
import React, { useState } from "react";
import { Input, TextArea, Button, Spinner } from "@heroui/react";
import { v6 as uuidv6 } from "uuid";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase/firebase";
import { cn } from "../util/cn";

// ─── Hugeicons ──────────────────────────────────────────────────────────────
import { HugeiconsIcon } from "@hugeicons/react";
import {
  UserIcon,
  Mail01Icon,
  CallIcon,
  Building01Icon,
  CodeIcon,
  Layout01Icon,
  SmartPhone01Icon,
  ShoppingCart01Icon,
  AnalyticsUpIcon,
  CustomerSupportIcon,
  DollarIcon,
  Tick01Icon,
  SentIcon,
  AlertCircleIcon,
  ArrowRight01Icon,
  ArrowLeft01Icon,
} from "@hugeicons/core-free-icons";

// ─── Data ───────────────────────────────────────────────────────────────────
const PROJECT_TYPES = [
  { value: "website", label: "Business Website", icon: Layout01Icon },
  { value: "ecommerce", label: "E-commerce Store", icon: ShoppingCart01Icon },
  { value: "webapp", label: "Web Application", icon: CodeIcon },
  { value: "mobile", label: "Mobile App", icon: SmartPhone01Icon },
  { value: "redesign", label: "Website Redesign", icon: AnalyticsUpIcon },
  { value: "other", label: "Other Project", icon: Building01Icon },
];

const TIMELINES = [
  { value: "asap", label: "ASAP", sub: "Expedited" },
  { value: "1-2months", label: "1–2 Months", sub: "Standard" },
  { value: "3-6months", label: "3–6 Months", sub: "Enterprise" },
  { value: "6months+", label: "6+ Months", sub: "Long-term" },
];

const SERVICES = [
  { name: "Web Development", icon: CodeIcon, desc: "Websites, portals & web apps" },
  { name: "Mobile App", icon: SmartPhone01Icon, desc: "iOS & Android development" },
  { name: "E-commerce", icon: ShoppingCart01Icon, desc: "Online stores & checkout" },
  { name: "UI/UX Design", icon: Layout01Icon, desc: "Interfaces & user experience" },
  { name: "SEO", icon: AnalyticsUpIcon, desc: "Search engine visibility" },
  { name: "Maintenance", icon: CustomerSupportIcon, desc: "Ongoing support & updates" },
];

const BUDGET_INR = [
  { value: "5k-10k", label: "₹5K – 10K", tier: "Starter" },
  { value: "10k-25k", label: "₹10K – 25K", tier: "Standard" },
  { value: "25k-50k", label: "₹25K – 50K", tier: "Advanced" },
  { value: "50k+", label: "₹50K+", tier: "Enterprise" },
];

const BUDGET_USD = [
  { value: "$80-$160", label: "$80 – $160", tier: "Starter" },
  { value: "$160-$290", label: "$160 – $290", tier: "Standard" },
  { value: "$290-$680", label: "$290 – $680", tier: "Advanced" },
  { value: "$700+", label: "$700+", tier: "Enterprise" },
];

// ─── Step meta ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Contact", icon: UserIcon },
  { label: "Project", icon: CodeIcon },
  { label: "Budget", icon: DollarIcon },
  { label: "Scope", icon: Layout01Icon },
  { label: "Review", icon: SentIcon },
];

// ─── Slide variants ─────────────────────────────────────────────────────────
const slide = {
  enter: (d: number) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
  center: {
    x: 0, opacity: 1,
    transition: { x: { type: "spring" as const, stiffness: 260, damping: 26 }, opacity: { duration: 0.2 } },
  },
  exit: (d: number) => ({
    x: d < 0 ? 40 : -40, opacity: 0,
    transition: { x: { type: "spring" as const, stiffness: 260, damping: 26 }, opacity: { duration: 0.15 } },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.055, delayChildren: 0.05 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 280, damping: 24 } },
};

// ─── Field wrapper ──────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}

function Field({ label, required, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium tracking-widest uppercase text-slate-500 dark:text-slate-400 select-none">
          {label}{required && <span className="text-rose-600 dark:text-rose-400 ml-1">*</span>}
        </label>
      </div>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            key="err"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="text-rose-600 dark:text-rose-400 text-xs flex items-center gap-1 overflow-hidden"
          >
            <HugeiconsIcon icon={AlertCircleIcon} className="w-3.5 h-3.5 shrink-0" size={14} />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Chip selector ──────────────────────────────────────────────────────────
interface ChipOptionProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  accent?: "blue" | "indigo" | "violet" | "rose" | "emerald" | "teal";
}

function ChipOption({ selected, onClick, children, accent = "blue" }: ChipOptionProps) {
  const colors = {
    blue: selected ? "border-blue-500 bg-blue-500/10 dark:bg-blue-500/8 text-blue-600 dark:text-blue-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
    indigo: selected ? "border-indigo-500 bg-indigo-500/10 dark:bg-indigo-500/8 text-indigo-600 dark:text-indigo-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
    violet: selected ? "border-violet-500 bg-violet-500/10 dark:bg-violet-500/8 text-violet-600 dark:text-violet-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
    rose: selected ? "border-rose-500 bg-rose-500/10 dark:bg-rose-500/8 text-rose-600 dark:text-rose-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
    emerald: selected ? "border-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/8 text-emerald-600 dark:text-emerald-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
    teal: selected ? "border-teal-500 bg-teal-500/10 dark:bg-teal-500/8 text-teal-600 dark:text-teal-300" : "border-slate-200 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/18 hover:text-slate-900 dark:hover:text-white",
  };
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      className={cn(
        "relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors duration-200 cursor-pointer outline-none select-none",
        colors[accent]
      )}
    >
      {selected && (
        <motion.div
          layoutId={`chip-${accent}`}
          className={cn("absolute inset-0 rounded-xl -z-10", {
            "bg-blue-500/8 dark:bg-blue-500/5": accent === "blue",
            "bg-indigo-500/8 dark:bg-indigo-500/5": accent === "indigo",
            "bg-violet-500/8 dark:bg-violet-500/5": accent === "violet",
            "bg-rose-500/8 dark:bg-rose-500/5": accent === "rose",
            "bg-emerald-500/8 dark:bg-emerald-500/5": accent === "emerald",
            "bg-teal-500/8 dark:bg-teal-500/5": accent === "teal",
          })}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
        />
      )}
      {children}
    </motion.button>
  );
}

// ─── Card option (icon card) ────────────────────────────────────────────────
interface CardOptionProps {
  selected: boolean;
  onClick: () => void;
  icon: any;
  title: string;
  subtitle?: string;
  accent: "blue" | "violet" | "rose" | "emerald";
}

function CardOption({ selected, onClick, icon: IcnComp, title, subtitle, accent }: CardOptionProps) {
  const ring = {
    blue: "border-blue-500/50 bg-blue-500/10 dark:bg-blue-500/5",
    violet: "border-violet-500/50 bg-violet-500/10 dark:bg-violet-500/5",
    rose: "border-rose-500/50 bg-rose-500/10 dark:bg-rose-500/5",
    emerald: "border-emerald-500/50 bg-emerald-500/10 dark:bg-emerald-500/5",
  };
  const iconColor = {
    blue: "text-blue-600 dark:text-blue-400",
    violet: "text-violet-600 dark:text-violet-400",
    rose: "text-rose-600 dark:text-rose-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
  };
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2, scale: 1.015 }}
      whileTap={{ scale: 0.975 }}
      className={cn(
        "relative text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer outline-none select-none w-full",
        selected ? ring[accent] : "border-slate-200 dark:border-white/6 hover:border-slate-300 dark:hover:border-white/14 bg-transparent hover:bg-slate-100/50 dark:hover:bg-white/2"
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className={cn("w-8 h-8 flex items-center justify-center rounded-lg",
          selected ? `${iconColor[accent]} bg-current/10` : "text-slate-400 dark:text-slate-500"
        )}>
          {IcnComp && <HugeiconsIcon icon={IcnComp} className="w-4 h-4" size={16} />}
        </div>
        {selected && (
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            className={cn("w-5 h-5 rounded-full flex items-center justify-center shrink-0", {
              "bg-blue-500": accent === "blue",
              "bg-violet-500": accent === "violet",
              "bg-rose-500": accent === "rose",
              "bg-emerald-500": accent === "emerald",
            })}
          >
            <HugeiconsIcon icon={Tick01Icon} className="w-3 h-3 text-white" size={12} />
          </motion.div>
        )}
      </div>
      <p className={cn("text-sm font-semibold font-outfit", selected ? {
        blue: "text-blue-700 dark:text-blue-200", violet: "text-violet-700 dark:text-violet-200", rose: "text-rose-700 dark:text-rose-200", emerald: "text-emerald-700 dark:text-emerald-200"
      }[accent] : "text-slate-800 dark:text-white")}>{title}</p>
      {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed font-outfit">{subtitle}</p>}
    </motion.button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function ContactForm() {
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    phone: string;
    company: string;
    projectType: string;
    budget: string;
    timeline: string;
    description: string;
    currency: "INR" | "USD";
    services: string[];
  }>({
    name: "", email: "", phone: "", company: "",
    projectType: "", budget: "", timeline: "", description: "",
    currency: "INR", services: [],
  });
  const [Id] = useState(() => uuidv6());
  const [[page, dir], setPage] = useState([0, 0]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== "undefined") {
      return document.documentElement.classList.contains("dark") ? "dark" : "light";
    }
    return "dark";
  });

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (typeof window !== "undefined") {
      if (next === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
  };

  const budgets = formData.currency === "INR" ? BUDGET_INR : BUDGET_USD;

  const set = (field: string, value: any) =>
    setFormData((p) => {
      const u = { ...p, [field]: value };
      if (field === "currency") u.budget = "";
      return u;
    });

  const touch = (f: string) => setTouched((p) => ({ ...p, [f]: true }));

  const touchStep = (s: number) => {
    const byStep = [
      ["name", "email", "phone", "company"],
      ["projectType", "timeline"],
      ["budget"],
      ["services", "description"],
      [],
    ];
    setTouched((p) => {
      const n = { ...p };
      byStep[s].forEach((f) => (n[f] = true));
      return n;
    });
  };

  const errFor = (field: string) => {
    if (!touched[field] && !submitAttempted) return "";
    const v = formData[field as keyof typeof formData];
    if (field === "name" && (typeof v !== "string" || !v.trim())) return "Name is required";
    if (field === "email") {
      if (typeof v !== "string" || !v.trim()) return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email";
    }
    if (field === "phone" && typeof v === "string" && v && !/^[+]?[1-9][\d]{0,15}$/.test(v.replace(/[\s\-()]/g, "")))
      return "Enter a valid phone number";
    if (field === "projectType" && !v) return "Select a project type";
    if (field === "timeline" && !v) return "Select a timeline";
    if (field === "budget" && !v) return "Select a budget range";
    if (field === "services" && formData.services.length === 0) return "Select at least one service";
    if (field === "description") {
      if (typeof v !== "string" || !v.trim()) return "Description is required";
      if (v.trim().length < 10) return "At least 10 characters";
    }
    return "";
  };

  const stepValid = (s: number): boolean => {
    switch (s) {
      case 0:
        return !!(formData.name.trim() && formData.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (!formData.phone || /^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-()]/g, ""))));
      case 1:
        return !!(formData.projectType && formData.timeline);
      case 2:
        return !!formData.budget;
      case 3:
        return formData.services.length > 0 && formData.description.trim().length >= 10;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const go = (d: number) => setPage(([p]) => [p + d, d]);

  const handleNext = () => {
    touchStep(page);
    if (stepValid(page)) go(1);
  };

  const handleBack = () => go(-1);

  const toggleService = (name: string) => {
    set("services", formData.services.includes(name)
      ? formData.services.filter((s) => s !== name)
      : [...formData.services, name]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    touchStep(3);
    if (!stepValid(3)) return;
    setIsSubmitting(true);
    try {
      const ref = doc(db, `/devebsy/${Id}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          id: Id, ...formData,
          createdAt: serverTimestamp(),
          updatedAt: new Date(),
        });
      }
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-[#080810] text-slate-900 dark:text-white transition-colors duration-300 relative overflow-hidden">
        {/* Ambient background glows */}
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-600/5 dark:bg-blue-900/8 blur-[160px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-indigo-600/5 dark:bg-indigo-900/8 blur-[140px]" />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
          className="relative rounded-4xl border border-slate-200 dark:border-white/[0.07] bg-white dark:bg-[#0d0d18]/80 backdrop-blur-2xl p-8 md:p-12 shadow-2xl shadow-slate-200/50 dark:shadow-black/50 max-w-lg w-full text-center"
        >
          {/* Animated Success Checkmark Ring */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="absolute inset-0 rounded-full bg-emerald-500/10 dark:bg-emerald-500/8 border border-emerald-500/20"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.4, 0] }}
              className="absolute inset-[-8px] rounded-full bg-emerald-500/20 blur-sm"
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            />
            <motion.svg
              className="absolute inset-4 w-16 h-16 text-emerald-600 dark:text-emerald-400"
              viewBox="0 0 52 52" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round"
            >
              <motion.circle cx={26} cy={26} r={23}
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: 0.55, ease: "easeOut", delay: 0.2 }}
              />
              <motion.path d="M16 27l7 7 14-14"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: 0.38, ease: "easeOut", delay: 0.7 }}
              />
            </motion.svg>
          </div>

          {/* Dynamic Personalized Greeting */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3 font-outfit tracking-tight"
          >
            Thanks, {formData.name.split(" ")[0]}!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="text-slate-600 dark:text-slate-400 text-sm md:text-base leading-relaxed font-outfit mb-8 max-w-sm mx-auto"
          >
            Your project brief has been received. We've sent a copy of your brief to <strong className="text-slate-800 dark:text-slate-200 font-semibold">{formData.email}</strong>.
          </motion.p>

          {/* Dynamic Progress/Next Steps Timeline */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="border-t border-slate-100 dark:border-white/[0.06] pt-6 mb-8 text-left space-y-4 max-w-sm mx-auto"
          >
            <p className="text-[10px] font-mono text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-4 text-center">Next Steps</p>
            
            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
                <HugeiconsIcon icon={Tick01Icon} className="w-3 h-3" size={12} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">Brief submitted</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-500">Details securely logged in Firestore</p>
              </div>
            </div>

            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/20 relative">
                <span className="absolute w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">Reviewing details</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-500">Our team is assessing scope & timeline</p>
              </div>
            </div>

            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-600 flex items-center justify-center shrink-0 border border-slate-200 dark:border-white/10">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-400 dark:text-slate-500">Tailored proposal</p>
                <p className="text-[11px] text-slate-400 dark:text-slate-600">Expected to arrive within 24 hours</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25 }}>
            <button
              onClick={() => {
                setFormData({ name:"",email:"",phone:"",company:"",projectType:"",budget:"",timeline:"",description:"",currency:"INR",services:[] });
                setSubmitAttempted(false); setTouched({}); setPage([0, 0]); setIsSubmitted(false);
              }}
              className="text-sm rounded-full bg-slate-900 dark:bg-white text-white dark:text-[#080810] px-6 py-2.5 font-semibold font-outfit shadow-md hover:bg-slate-800 dark:hover:bg-white/90 transition-all duration-200 cursor-pointer outline-none inline-flex items-center gap-2"
            >
              Start new project brief
              <HugeiconsIcon icon={ArrowRight01Icon} className="w-4 h-4" size={16} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ── Main layout ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#080810] text-slate-900 dark:text-white transition-colors duration-300">
      {/* Subtle ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-600/5 dark:bg-blue-900/8 blur-[160px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-indigo-600/5 dark:bg-indigo-900/8 blur-[140px]" />
      </div>

      {/* Floating Theme Toggle */}
      <div className="absolute top-6 right-6 z-50">
        <motion.button
          type="button"
          onClick={toggleTheme}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-3 rounded-full border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#0d0d18]/80 backdrop-blur-md text-slate-600 dark:text-slate-300 shadow-md hover:bg-slate-100 dark:hover:bg-white/5 cursor-pointer outline-none"
        >
          {theme === "dark" ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M14.25 12a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </motion.button>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-20 md:py-32">

        {/* ── Header ───────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-16 text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-slate-500 dark:text-slate-500 font-mono mb-4">
            devebsy / start a project
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight font-outfit leading-tight mb-4">
            Tell us what you're building.
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-base max-w-xl mx-auto font-outfit leading-relaxed">
            Five quick steps. We'll respond within 24 hours with a detailed proposal.
          </p>
        </motion.div>

        {/* ── Step indicator ───────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.4 }}
          className="flex items-center gap-0 mb-12 max-w-lg mx-auto"
        >
          {STEPS.map((step, i) => {
            const done = i < page;
            const active = i === page;
            return (
              <div key={step.label} className="flex items-center flex-1 last:flex-none">
                <button
                  type="button"
                  onClick={() => {
                    if (i < page) setPage([i, -1]);
                    else if (i > page) {
                      let ok = true;
                      for (let k = page; k < i; k++) if (!stepValid(k)) { ok = false; touchStep(k); break; }
                      if (ok) setPage([i, 1]);
                    }
                  }}
                  className="flex flex-col items-center gap-1.5 group cursor-pointer outline-none"
                >
                  <div className={cn(
                    "relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 border",
                    active ? "border-slate-400 dark:border-white/30 bg-slate-100 dark:bg-white/6 text-slate-900 dark:text-white" :
                    done ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                    "border-slate-200 dark:border-white/8 text-slate-400 dark:text-slate-600 group-hover:border-slate-300 dark:group-hover:border-white/16 group-hover:text-slate-600 dark:group-hover:text-slate-400"
                  )}>
                    {active && (
                      <motion.div
                        className="absolute inset-0 rounded-full border border-slate-300 dark:border-white/20"
                        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                      />
                    )}
                    {done
                      ? <HugeiconsIcon icon={Tick01Icon} className="w-4 h-4" size={16} />
                      : <HugeiconsIcon icon={step.icon} className="w-4 h-4" size={16} />
                    }
                  </div>
                  <span className={cn(
                    "text-[9px] font-mono tracking-widest uppercase transition-colors duration-200",
                    active ? "text-slate-900 dark:text-white font-semibold" : done ? "text-emerald-600 dark:text-emerald-500" : "text-slate-400 dark:text-slate-600"
                  )}>{step.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className="relative flex-1 mx-2 mb-4">
                    <div className="h-px bg-slate-200 dark:bg-white/6" />
                    <motion.div
                      className="absolute inset-0 h-px bg-emerald-500/50"
                      animate={{ scaleX: i < page ? 1 : 0 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      style={{ originX: 0 }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </motion.div>

        {/* ── Form card ────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.45 }}
        >
          <form onSubmit={handleSubmit}>
            <div className="rounded-4xl border border-slate-200 dark:border-white/[0.07] bg-white dark:bg-[#0d0d18]/80 backdrop-blur-2xl overflow-hidden shadow-2xl shadow-slate-200/50 dark:shadow-black/50">

              {/* Step label bar */}
              <div className="flex items-center justify-between px-8 py-4 border-b border-slate-100 dark:border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-slate-500 dark:text-slate-600">
                    {String(page + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                  </span>
                  <span className="w-px h-4 bg-slate-200 dark:bg-white/8" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 font-outfit uppercase tracking-widest">
                    {STEPS[page].label}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="relative w-32 h-0.5 bg-slate-100 dark:bg-white/6 rounded-full overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                    animate={{ width: `${((page) / (STEPS.length - 1)) * 100}%` }}
                    transition={{ type: "spring", stiffness: 180, damping: 24 }}
                  />
                </div>
              </div>

              {/* Steps content */}
              <div className="px-8 py-10 min-h-[420px]">
                <AnimatePresence mode="wait" initial={false} custom={dir}>
                  <motion.div
                    key={page}
                    custom={dir}
                    variants={slide}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    {/* ── Step 0: Contact ─────────────────────── */}
                    {page === 0 && (
                      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-6">
                        <motion.p variants={fadeUp} className="text-slate-500 dark:text-slate-400 text-sm font-outfit mb-8">
                          How should we get in touch with you?
                        </motion.p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <motion.div variants={fadeUp}>
                            <Field label="Full name" required error={errFor("name")}>
                              <div className="relative">
                                <Input
                                  value={formData.name}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("name", e.target.value)}
                                  onBlur={() => touch("name")}
                                  placeholder="e.g. Rahul Sharma"
                                  required
                                  className="w-full bg-slate-50 dark:bg-white/3 border border-slate-200 dark:border-white/8 hover:border-slate-300 dark:hover:border-white/14 focus:border-blue-500 dark:focus:border-blue-500/60 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-550 transition-all duration-200 font-outfit outline-none pr-10"
                                />
                                <HugeiconsIcon icon={UserIcon} className="absolute right-3.5 top-3.5 w-4 h-4 text-slate-400 dark:text-slate-600" size={16} />
                              </div>
                            </Field>
                          </motion.div>

                          <motion.div variants={fadeUp}>
                            <Field label="Email address" required error={errFor("email")}>
                              <div className="relative">
                                <Input
                                  type="email"
                                  value={formData.email}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("email", e.target.value)}
                                  onBlur={() => touch("email")}
                                  placeholder="rahul@example.com"
                                  required
                                  className="w-full bg-slate-50 dark:bg-white/3 border border-slate-200 dark:border-white/8 hover:border-slate-300 dark:hover:border-white/14 focus:border-blue-500 dark:focus:border-blue-500/60 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-550 transition-all duration-200 font-outfit outline-none pr-10"
                                />
                                <HugeiconsIcon icon={Mail01Icon} className="absolute right-3.5 top-3.5 w-4 h-4 text-slate-400 dark:text-slate-600" size={16} />
                              </div>
                            </Field>
                          </motion.div>

                          <motion.div variants={fadeUp}>
                            <Field label="Phone number" error={errFor("phone")}>
                              <div className="relative">
                                <Input
                                  type="tel"
                                  value={formData.phone}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("phone", e.target.value)}
                                  onBlur={() => touch("phone")}
                                  placeholder="+91 98765 43210"
                                  className="w-full bg-slate-50 dark:bg-white/3 border border-slate-200 dark:border-white/8 hover:border-slate-300 dark:hover:border-white/14 focus:border-blue-500 dark:focus:border-blue-500/60 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-550 transition-all duration-200 font-outfit outline-none pr-10"
                                />
                                <HugeiconsIcon icon={CallIcon} className="absolute right-3.5 top-3.5 w-4 h-4 text-slate-400 dark:text-slate-600" size={16} />
                              </div>
                            </Field>
                          </motion.div>

                          <motion.div variants={fadeUp}>
                            <Field label="Company / Brand">
                              <div className="relative">
                                <Input
                                  value={formData.company}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("company", e.target.value)}
                                  placeholder="Optional"
                                  className="w-full bg-slate-50 dark:bg-white/3 border border-slate-200 dark:border-white/8 hover:border-slate-300 dark:hover:border-white/14 focus:border-blue-500 dark:focus:border-blue-500/60 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-550 transition-all duration-200 font-outfit outline-none pr-10"
                                />
                                <HugeiconsIcon icon={Building01Icon} className="absolute right-3.5 top-3.5 w-4 h-4 text-slate-400 dark:text-slate-600" size={16} />
                              </div>
                            </Field>
                          </motion.div>
                        </div>
                      </motion.div>
                    )}

                    {/* ── Step 1: Project ─────────────────────── */}
                    {page === 1 && (
                      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-8">
                        <motion.p variants={fadeUp} className="text-slate-500 dark:text-slate-400 text-sm font-outfit">
                          What are we building, and when do you need it?
                        </motion.p>

                        <motion.div variants={fadeUp} className="space-y-3">
                          <Field label="Project type" required error={errFor("projectType")}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-1">
                              {PROJECT_TYPES.map((pt) => (
                                <CardOption
                                  key={pt.value}
                                  selected={formData.projectType === pt.value}
                                  onClick={() => set("projectType", pt.value)}
                                  icon={pt.icon}
                                  title={pt.label}
                                  accent="blue"
                                />
                              ))}
                            </div>
                          </Field>
                        </motion.div>

                        <motion.div variants={fadeUp} className="space-y-3">
                          <Field label="Timeline" required error={errFor("timeline")}>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {TIMELINES.map((t) => (
                                <ChipOption
                                  key={t.value}
                                  selected={formData.timeline === t.value}
                                  onClick={() => set("timeline", t.value)}
                                  accent="indigo"
                                >
                                  <span>{t.label}</span>
                                  <span className="text-xs opacity-60">{t.sub}</span>
                                </ChipOption>
                              ))}
                            </div>
                          </Field>
                        </motion.div>
                      </motion.div>
                    )}

                    {/* ── Step 2: Budget ──────────────────────── */}
                    {page === 2 && (
                      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-7">
                        <motion.p variants={fadeUp} className="text-slate-500 dark:text-slate-400 text-sm font-outfit">
                          What's your approximate investment range?
                        </motion.p>

                        {/* Currency toggle */}
                        <motion.div variants={fadeUp}>
                          <div className="inline-flex bg-slate-100 dark:bg-white/3 border border-slate-200 dark:border-white/8 rounded-xl p-1 gap-1">
                            {[{ v: "INR", l: "₹ INR" }, { v: "USD", l: "$ USD" }].map(({ v, l }) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => set("currency", v)}
                                className={cn(
                                  "relative px-5 py-2 text-xs font-semibold rounded-lg transition-colors duration-200 cursor-pointer outline-none font-mono",
                                  formData.currency === v ? "text-slate-900 dark:text-white" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                )}
                              >
                                {formData.currency === v && (
                                  <motion.div
                                    layoutId="currency-pill"
                                    className="absolute inset-0 bg-white dark:bg-white/8 shadow-sm dark:shadow-none rounded-lg -z-10"
                                    transition={{ type: "spring", stiffness: 360, damping: 28 }}
                                  />
                                )}
                                {l}
                              </button>
                            ))}
                          </div>
                        </motion.div>

                        <motion.div variants={fadeUp}>
                          <Field label="Budget range" required error={errFor("budget")}>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
                              {budgets.map((b) => (
                                <motion.button
                                  key={b.value}
                                  type="button"
                                  onClick={() => set("budget", b.value)}
                                  whileHover={{ y: -2 }}
                                  whileTap={{ scale: 0.97 }}
                                  className={cn(
                                    "text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer outline-none select-none",
                                    formData.budget === b.value
                                      ? "border-rose-500/40 bg-rose-500/10 dark:bg-rose-500/6"
                                      : "border-slate-200 dark:border-white/6 hover:border-slate-300 dark:hover:border-white/14"
                                  )}
                                >
                                  <p className="text-[10px] font-mono text-slate-500 dark:text-slate-600 uppercase tracking-widest mb-2">{b.tier}</p>
                                  <p className={cn("text-base font-bold font-outfit",
                                    formData.budget === b.value ? "text-rose-700 dark:text-rose-200" : "text-slate-800 dark:text-white"
                                  )}>{b.label}</p>
                                  {formData.budget === b.value && (
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{ width: "2rem" }}
                                      className="mt-2 h-0.5 bg-rose-500/60 rounded-full"
                                      transition={{ duration: 0.3 }}
                                    />
                                  )}
                                </motion.button>
                              ))}
                            </div>
                          </Field>
                        </motion.div>
                      </motion.div>
                    )}

                    {/* ── Step 3: Scope ───────────────────────── */}
                    {page === 3 && (
                      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-8">
                        <motion.p variants={fadeUp} className="text-slate-500 dark:text-slate-400 text-sm font-outfit">
                          Which services do you need, and what's the brief?
                        </motion.p>

                        <motion.div variants={fadeUp}>
                          <Field label="Services needed" required error={errFor("services")}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
                              {SERVICES.map((svc) => (
                                <CardOption
                                  key={svc.name}
                                  selected={formData.services.includes(svc.name)}
                                  onClick={() => toggleService(svc.name)}
                                  icon={svc.icon}
                                  title={svc.name}
                                  subtitle={svc.desc}
                                  accent="violet"
                                />
                              ))}
                            </div>
                          </Field>
                        </motion.div>

                        <motion.div variants={fadeUp}>
                          <Field label="Project description" required error={errFor("description")}>
                            <div className="relative mt-1">
                              <TextArea
                                value={formData.description}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("description", e.target.value)}
                                onBlur={() => touch("description")}
                                placeholder="Describe what you're building, your goals, target audience, references or any inspiration…"
                                required
                                className="w-full bg-slate-50 dark:bg-white/3 border border-slate-200 dark:border-white/8 hover:border-slate-300 dark:hover:border-white/14 focus:border-violet-500 dark:focus:border-violet-500/50 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-550 transition-all duration-200 font-outfit outline-none resize-none leading-relaxed min-h-[120px]"
                              />
                              <div className={cn(
                                "absolute bottom-3 right-4 text-xs font-mono transition-colors duration-200",
                                formData.description.trim().length >= 10 ? "text-emerald-600 dark:text-emerald-500" : "text-slate-400 dark:text-slate-700"
                              )}>
                                {formData.description.trim().length} / 10
                              </div>
                            </div>
                          </Field>
                        </motion.div>
                      </motion.div>
                    )}

                    {/* ── Step 4: Review ──────────────────────── */}
                    {page === 4 && (
                      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5">
                        <motion.p variants={fadeUp} className="text-slate-500 dark:text-slate-400 text-sm font-outfit mb-2">
                          Everything looks good? Hit send to submit your brief.
                        </motion.p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Contact */}
                          <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-slate-200 dark:border-white/6 bg-slate-50/50 dark:bg-white/2 space-y-3">
                            <p className="text-[10px] font-mono text-slate-500 dark:text-slate-600 tracking-widest uppercase">Contact</p>
                            <div className="space-y-2.5">
                              {[
                                { label: "Name", value: formData.name },
                                { label: "Email", value: formData.email },
                                formData.phone && { label: "Phone", value: formData.phone },
                                formData.company && { label: "Company", value: formData.company },
                              ].filter(Boolean).map((item) => {
                                const val = item ? item.value : "";
                                const label = item ? item.label : "";
                                return (
                                  <div key={label} className="flex justify-between text-sm font-outfit gap-4">
                                    <span className="text-slate-500 shrink-0">{label}</span>
                                    <span className="text-slate-800 dark:text-slate-200 text-right truncate font-medium">{val}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>

                          {/* Project */}
                          <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-slate-200 dark:border-white/6 bg-slate-50/50 dark:bg-white/2 space-y-3">
                            <p className="text-[10px] font-mono text-slate-500 dark:text-slate-600 tracking-widest uppercase">Project</p>
                            <div className="space-y-2.5">
                              {[
                                { label: "Type", value: PROJECT_TYPES.find(p => p.value === formData.projectType)?.label },
                                { label: "Timeline", value: TIMELINES.find(t => t.value === formData.timeline)?.label },
                                { label: "Budget", value: budgets.find(b => b.value === formData.budget)?.label },
                              ].filter(v => v.value).map(({ label, value }) => (
                                <div key={label} className="flex justify-between text-sm font-outfit gap-4">
                                  <span className="text-slate-500 shrink-0">{label}</span>
                                  <span className="text-slate-800 dark:text-slate-200 text-right font-medium">{value}</span>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        </div>

                        {/* Services */}
                        <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-slate-200 dark:border-white/6 bg-slate-50/50 dark:bg-white/2 space-y-3">
                          <p className="text-[10px] font-mono text-slate-500 dark:text-slate-600 tracking-widest uppercase">Services</p>
                          <div className="flex flex-wrap gap-2">
                            {formData.services.map((s) => (
                              <span key={s} className="px-3 py-1.5 text-xs font-outfit font-medium rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-600 dark:text-violet-300">
                                {s}
                              </span>
                            ))}
                          </div>
                        </motion.div>

                        {/* Description */}
                        <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-slate-200 dark:border-white/6 bg-slate-50/50 dark:bg-white/2 space-y-3">
                          <p className="text-[10px] font-mono text-slate-500 dark:text-slate-600 tracking-widest uppercase">Brief</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300 font-outfit leading-relaxed whitespace-pre-line">
                            {formData.description}
                          </p>
                        </motion.div>
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* ── Footer nav ──────────────────────────────── */}
              <div className="flex min-w-full items-center justify-between px-8 py-5 border-t border-slate-100 dark:border-white/[0.06]">
                {page > 0 ? (
                  <motion.button
                    type="button"
                    onClick={handleBack}
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.96 }}
                    className="flex items-center gap-2 text-sm rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors duration-200 font-outfit cursor-pointer outline-none"
                  >
                    <HugeiconsIcon icon={ArrowLeft01Icon} className="w-4 h-4" size={16} />
                    Back
                  </motion.button>
                ) : <div />}

                {page < 4 ? (
                  <motion.button
                    type="button"
                    onClick={handleNext}
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-[#080810] text-sm font-semibold font-outfit hover:bg-slate-800 dark:hover:bg-white/90 transition-colors duration-200 cursor-pointer outline-none"
                  >
                    Continue
                    <HugeiconsIcon icon={ArrowRight01Icon} className="w-4 h-4" size={16} />
                  </motion.button>
                ) : (
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      type="submit"
                      isDisabled={isSubmitting}
                      className="flex items-center gap-2.5 px-7 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 dark:hover:from-blue-400 dark:hover:to-indigo-400 text-white text-sm font-semibold font-outfit transition-all duration-200 cursor-pointer outline-none disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" color="current" />
                          <span>Sending…</span>
                        </>
                      ) : (
                        <>
                          <HugeiconsIcon icon={SentIcon} className="w-4 h-4" size={16} />
                          <span>Send project brief</span>
                        </>
                      )}
                    </Button>
                  </motion.div>
                )}
              </div>

            </div>
          </form>
        </motion.div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center text-xs text-slate-500 dark:text-slate-700 font-outfit mt-8"
        >
          We respond within 24 hours · All inquiries kept confidential
        </motion.p>

      </div>
    </div>
  );
}